import {
  Certification,
  ContactInfo,
  Education,
  InterestCategory,
  ProfessionalSummaryEntry,
  Project,
  Resume,
  ResumeSectionType,
  SkillCategory,
  WorkExperienceCompany,
  normalizeContactInfo,
} from '@applicator/shared'
import { useState, useCallback, useRef, useEffect } from 'react'

import {
  fetchAccountWorkExperience,
  syncAccountWorkExperienceToAllResumes,
  workExperienceHasContent,
} from '../../lib/accountWorkExperience'
import {
  educationHasContent,
  fetchAccountEducation,
  isEducationCatalogEqual,
  mergeEducationCatalogWithResumeInclusions,
  syncAccountEducationCatalogToAllResumes,
  unionEducationCatalogs,
} from '../../lib/accountEducation'
import {
  fetchAccountSkills,
  isSkillsCatalogEqual,
  mergeCatalogWithResumeInclusions,
  skillsHasContent,
  syncAccountSkillsCatalogToAllResumes,
  unionSkillsCatalogs,
} from '../../lib/accountSkills'
import { sanitizeWorkExperienceCompanies } from '../../utils/workExperienceGroup'
import { normalizeWorkExperienceInclusionHierarchy } from '../../utils/workExperienceInclusion'
import { persistResumeSection, type SaveResumeContentResult } from '../../lib/resumeContentPersist'
import { supabase } from '../../lib/supabase'
import { showError as showToastError } from '../../lib/toast'
import { normalizeCertificationsList } from '../../utils/certificationInclusion'

import { RESUME_SECTIONS } from './constants'
import { createEmptyResumeSectionData } from './emptyResumeData'
import type { Interest } from './InterestsEditor'

type ResumeSectionData =
  | ContactInfo
  | string[]
  | ProfessionalSummaryEntry[]
  | WorkExperienceCompany[]
  | Education[]
  | SkillCategory[]
  | Interest[]
  | Certification[]
  | Project[]
  | unknown[]

export type LoadResumeOptions = {
  /** Background refresh during optimization — keep UI mounted, no toast on failure. */
  silent?: boolean
}

export type { SaveResumeContentResult }

export function useResumeData(resumeId: string | undefined) {
  const normalizedResumeId = resumeId?.replace(/\.+$/, '')
  const [resume, setResume] = useState<Resume | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [resumeData, setResumeData] = useState<Record<ResumeSectionType, ResumeSectionData>>(
    () => createEmptyResumeSectionData() as Record<ResumeSectionType, ResumeSectionData>,
  )

  const loadGenerationRef = useRef(0)
  const saveGenerationRef = useRef(0)
  const loadingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const loadInFlightRef = useRef<Promise<void> | null>(null)
  const loadTargetResumeIdRef = useRef<string | undefined>(undefined)
  const pendingSilentReloadRef = useRef(false)
  const activeResumeIdRef = useRef(normalizedResumeId)
  activeResumeIdRef.current = normalizedResumeId
  const resumeDataRef = useRef(resumeData)
  resumeDataRef.current = resumeData

  useEffect(() => {
    loadGenerationRef.current += 1
    saveGenerationRef.current += 1
    loadTargetResumeIdRef.current = undefined
    loadInFlightRef.current = null
    pendingSilentReloadRef.current = false
    setResume(null)
    setResumeData(createEmptyResumeSectionData() as Record<ResumeSectionType, ResumeSectionData>)
    setError(null)
    if (normalizedResumeId) {
      setLoading(true)
    } else {
      setLoading(false)
    }
  }, [normalizedResumeId])

  const clearLoadingTimeout = useCallback(() => {
    if (loadingTimeoutRef.current) {
      clearTimeout(loadingTimeoutRef.current)
      loadingTimeoutRef.current = null
    }
  }, [])

  const getActiveResumeId = useCallback(() => activeResumeIdRef.current, [])

  const loadResume = useCallback(
    async (options?: LoadResumeOptions) => {
      const silent = options?.silent ?? false
      if (!normalizedResumeId) return

      if (
        loadInFlightRef.current &&
        loadTargetResumeIdRef.current === normalizedResumeId
      ) {
        if (silent) {
          pendingSilentReloadRef.current = true
        }
        return loadInFlightRef.current
      }

      const generation = ++loadGenerationRef.current
      loadTargetResumeIdRef.current = normalizedResumeId
      clearLoadingTimeout()

      if (!silent) {
        setLoading(true)
        setError(null)
      }

      loadingTimeoutRef.current = setTimeout(() => {
        if (generation !== loadGenerationRef.current) return
        if (silent) return
        setError('Loading timeout. Please refresh the page.')
        setLoading(false)
      }, 20000)

      const runLoad = async () => {
        const targetResumeId = normalizedResumeId
        try {
          const {
            data: { session },
          } = await supabase.auth.getSession()
          if (generation !== loadGenerationRef.current) return

          const user = session?.user
          if (!user) {
            if (!silent) {
              setError('User not authenticated')
            }
            return
          }

          const { data: resumeRow, error: resumeError } = await supabase
            .from('resumes')
            .select('*')
            .eq('id', targetResumeId)
            .eq('user_id', user.id)
            .single()

          if (generation !== loadGenerationRef.current) return

          if (resumeError) throw resumeError
          if (!resumeRow) {
            if (!silent) {
              setError('Resume not found')
            }
            return
          }

          setResume({
            id: resumeRow.id,
            title: resumeRow.title,
            createdAt: resumeRow.created_at,
            updatedAt: resumeRow.updated_at,
            isTemplate: Boolean(resumeRow.is_template),
          })

          const { data: contentData, error: contentError } = await supabase
            .from('resume_content')
            .select('*')
            .eq('resume_id', targetResumeId)
            .order('display_order', { ascending: true })

          if (generation !== loadGenerationRef.current) return

          if (contentError) throw contentError

          const initialData = createEmptyResumeSectionData() as Record<
            ResumeSectionType,
            ResumeSectionData
          >

          contentData?.forEach((item) => {
            let content = item.content

            if (
              item.section_type === 'interests' &&
              Array.isArray(content) &&
              content.length > 0
            ) {
              if (typeof content[0] === 'string') {
                content = content.map((interest: string, index: number) => ({
                  id: `interest-${Date.now()}-${index}`,
                  name: interest,
                  included: true,
                }))
              } else if (content[0]?.interests && Array.isArray(content[0].interests)) {
                const allInterests: Interest[] = []
                content.forEach((category: InterestCategory) => {
                  if (category.interests && Array.isArray(category.interests)) {
                    category.interests.forEach((interest) => {
                      allInterests.push({
                        id: interest.id || `interest-${Date.now()}-${crypto.randomUUID()}`,
                        name: interest.name,
                        included: interest.included ?? true,
                      })
                    })
                  }
                })
                content = allInterests
              }
            }

            if (item.section_type === 'certifications' && Array.isArray(content)) {
              content = normalizeCertificationsList(content as Certification[])
            }

            if (item.section_type === 'contact_info' && content && typeof content === 'object') {
              content = normalizeContactInfo(content as ContactInfo)
            }

            initialData[item.section_type as ResumeSectionType] = content
          })

          const accountWorkExperience = await fetchAccountWorkExperience(supabase)
          if (accountWorkExperience && workExperienceHasContent(accountWorkExperience)) {
            initialData.work_experience = sanitizeWorkExperienceCompanies(
              normalizeWorkExperienceInclusionHierarchy(accountWorkExperience),
            )
          } else if (Array.isArray(initialData.work_experience)) {
            initialData.work_experience = sanitizeWorkExperienceCompanies(
              normalizeWorkExperienceInclusionHierarchy(
                initialData.work_experience as WorkExperienceCompany[],
              ),
            )
          }

          const resumeSkills = Array.isArray(initialData.skills)
            ? (initialData.skills as SkillCategory[])
            : []
          const accountSkills = await fetchAccountSkills(supabase)
          if (resumeSkills.length > 0 && skillsHasContent(resumeSkills)) {
            const catalogBase =
              accountSkills && skillsHasContent(accountSkills)
                ? unionSkillsCatalogs(accountSkills, resumeSkills)
                : resumeSkills
            initialData.skills = mergeCatalogWithResumeInclusions(catalogBase, resumeSkills)
          } else if (accountSkills && skillsHasContent(accountSkills)) {
            initialData.skills = mergeCatalogWithResumeInclusions(accountSkills, resumeSkills)
          }

          const resumeEducation = Array.isArray(initialData.education)
            ? (initialData.education as Education[])
            : []
          const accountEducation = await fetchAccountEducation(supabase)
          if (resumeEducation.length > 0 && educationHasContent(resumeEducation)) {
            const catalogBase =
              accountEducation && educationHasContent(accountEducation)
                ? unionEducationCatalogs(accountEducation, resumeEducation)
                : resumeEducation
            initialData.education = mergeEducationCatalogWithResumeInclusions(
              catalogBase,
              resumeEducation,
            )
          } else if (accountEducation && educationHasContent(accountEducation)) {
            initialData.education = mergeEducationCatalogWithResumeInclusions(
              accountEducation,
              resumeEducation,
            )
          }

          setResumeData(initialData)
        } catch (err) {
          if (generation !== loadGenerationRef.current) return
          const errorMsg = err instanceof Error ? err.message : 'Failed to load resume'
          if (!silent) {
            setError(errorMsg)
            showToastError(errorMsg)
          } else {
            console.warn('[useResumeData] silent reload failed:', errorMsg)
          }
        } finally {
          if (generation !== loadGenerationRef.current) return
          clearLoadingTimeout()
          if (!silent) {
            setLoading(false)
          }
        }
      }

      const promise = runLoad().finally(() => {
        if (loadTargetResumeIdRef.current === normalizedResumeId) {
          loadInFlightRef.current = null
        }
        if (pendingSilentReloadRef.current) {
          pendingSilentReloadRef.current = false
          void loadResume({ silent: true })
        }
      })
      loadInFlightRef.current = promise
      return promise
    },
    [clearLoadingTimeout, normalizedResumeId],
  )

  const saveResumeContent = useCallback(
    async (
      sectionType: ResumeSectionType,
      content: Record<string, unknown>,
      skipStateUpdate = false,
      options?: { targetResumeId?: string },
    ): Promise<SaveResumeContentResult> => {
      const targetResumeId = options?.targetResumeId ?? normalizedResumeId
      if (!targetResumeId) {
        return { ok: false, error: 'Resume id missing' }
      }

      const saveGenerationAtStart = saveGenerationRef.current

      const displayOrder = RESUME_SECTIONS.findIndex((s) => s.type === sectionType)
      let payload: Record<string, unknown> = content
      if (sectionType === 'work_experience') {
        payload = sanitizeWorkExperienceCompanies(
          normalizeWorkExperienceInclusionHierarchy(content as WorkExperienceCompany[]),
        ) as unknown as Record<string, unknown>
      }

      const previousSkills =
        sectionType === 'skills' && Array.isArray(resumeDataRef.current.skills)
          ? (resumeDataRef.current.skills as SkillCategory[])
          : null
      const nextSkills =
        sectionType === 'skills' && Array.isArray(content)
          ? (content as SkillCategory[])
          : null
      const skillsCatalogChanged =
        sectionType === 'skills' &&
        previousSkills !== null &&
        nextSkills !== null &&
        !isSkillsCatalogEqual(previousSkills, nextSkills)

      const previousEducation =
        sectionType === 'education' && Array.isArray(resumeDataRef.current.education)
          ? (resumeDataRef.current.education as Education[])
          : null
      const nextEducation =
        sectionType === 'education' && Array.isArray(content)
          ? (content as Education[])
          : null
      const educationCatalogChanged =
        sectionType === 'education' &&
        previousEducation !== null &&
        nextEducation !== null &&
        !isEducationCatalogEqual(previousEducation, nextEducation)

      const result = await persistResumeSection(
        targetResumeId,
        sectionType,
        payload,
        displayOrder,
      )

      if (saveGenerationAtStart !== saveGenerationRef.current) {
        return { ok: true }
      }

      if (result.ok && sectionType === 'work_experience') {
        try {
          await syncAccountWorkExperienceToAllResumes(
            supabase,
            payload as WorkExperienceCompany[],
          )
        } catch (syncError) {
          const message =
            syncError instanceof Error
              ? syncError.message
              : 'Failed to sync work experience across resumes'
          return { ok: false, error: message }
        }
      }

      if (result.ok && sectionType === 'skills' && skillsCatalogChanged && nextSkills) {
        try {
          await syncAccountSkillsCatalogToAllResumes(supabase, nextSkills, {
            sourceResumeId: targetResumeId,
            sourceResumeSkills: nextSkills,
          })
        } catch (syncError) {
          const message =
            syncError instanceof Error
              ? syncError.message
              : 'Failed to sync skills catalog across resumes'
          return { ok: false, error: message }
        }
      }

      if (result.ok && sectionType === 'education' && educationCatalogChanged && nextEducation) {
        try {
          await syncAccountEducationCatalogToAllResumes(supabase, nextEducation, {
            sourceResumeId: targetResumeId,
            sourceResumeEducation: nextEducation,
          })
        } catch (syncError) {
          const message =
            syncError instanceof Error
              ? syncError.message
              : 'Failed to sync education catalog across resumes'
          return { ok: false, error: message }
        }
      }

      if (result.ok && !skipStateUpdate && activeResumeIdRef.current === targetResumeId) {
        setResumeData((prev) => ({
          ...prev,
          [sectionType]: payload,
        }))
      }

      return result
    },
    [normalizedResumeId],
  )

  return {
    resume,
    loading,
    error,
    resumeData,
    setResume,
    setResumeData,
    loadResume,
    saveResumeContent,
    getActiveResumeId,
  }
}
